import { useState, useCallback } from "react";
import {
  JobFormData,
  JobFormErrors,
  UseJobFormReturn,
  JobStep,
} from "../types/add-job.types";
import {
  pitchValuePattern,
  squaresValuePattern,
} from "../constants/add-job.constants";
const initialFormData: JobFormData = {
  existenceCustomer: "Existing",
  firstName: "",
  lastName: "",
  name: "",
  phoneNumber: "",
  email: "",
  leadSource: "",
  address: "",
  latitude: 0,
  longitude: 0,
  postalCode: "",
  jobTitle: "",
  jobNumber: "",
  jobName: "",
  jobType: "",
  priority: "",
  jobStage: "",
  salesRepId: "",
  jobAssignedToId: "",
  trade: "",
  customerId: "",
  needAppointment: false,
  referralName: "",
  tradeType: [],
  shingle: "",
  customShingleColor: "",
  shinglesManufacturer: "",
  numberOfSquares: "",
  currentStage: "",
  pitch: "",
  jobDescription: "",
  notes: "",
  insuranceCompanyName: "",
  claimNumber: "",
  adjusterName: "",
  policyType: "",
  insuranceEmail: "",
  insurancePhoneNumber: "",
};

const initialErrors: JobFormErrors = {
  name: "",
  phoneNumber: "",
  email: "",
  address: "",
  leadSource: "",
  jobTitle: "",
  jobNumber: "",
  jobName: "",
  jobType: "",
  priority: "",
  jobStage: "",
  salesRepId: "",
  jobAssignedToId: "",
  trade: "",
  tradeType: "",
  currentStage: "",
  referralName: "",
  shingle: "",
  shinglesManufacturer: "",
  numberOfSquares: "",
  pitch: "",
  jobDescription: "",
  insuranceCompanyName: "",
  insuranceEmail: "",
  claimNumber: "",
  insurancePhoneNumber: "",
  policyType: "",
};

const createEmptyErrors = (): JobFormErrors => ({ ...initialErrors });

export const useJobForm = (saved?: Partial<JobFormData>): UseJobFormReturn => {
  const [formData, setFormData] = useState<JobFormData>({
    ...initialFormData,
    ...saved,
  });
  const [errors, setErrors] = useState<JobFormErrors>(initialErrors);

  const updateField = useCallback(
    (field: keyof JobFormData, value: any) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const updateError = useCallback(
    (field: keyof JobFormErrors, value: string) => {
      setErrors((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const validateJobStep = useCallback(
    (jobStep: JobStep): boolean => {
      const newError = createEmptyErrors();

      if (jobStep === "New") {
        if (!formData.name) {
          newError.name = "Name is required";
        }
        if (!formData.phoneNumber) {
          newError.phoneNumber = "Phone number is required";
        } else if (!/^\d+$/.test(formData.phoneNumber)) {
          newError.phoneNumber = "Phone number must be numeric";
        }
        if (!formData.email) {
          newError.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          newError.email = "Email is invalid";
        }
      }

      setErrors(newError);
      return Object.values(newError).every((v) => v === "");
    },
    [formData]
  );

  const isDetailsStepValid = useCallback((): boolean => {
    const hasRequiredFields =
      !!formData.jobType &&
      !!formData.salesRepId &&
      !!formData.leadSource &&
      formData.tradeType.length > 0 &&
      !!formData.jobDescription.trim();

    const hasValidOptionalFormats =
      (!formData.numberOfSquares ||
        squaresValuePattern.test(formData.numberOfSquares)) &&
      (!formData.pitch || pitchValuePattern.test(formData.pitch));

    return hasRequiredFields && hasValidOptionalFormats;
  }, [formData]);

  const validateDetailsStep = useCallback((): boolean => {
    const newError = createEmptyErrors();

    if (!formData.jobType) {
      newError.jobType = "Job type is required";
    }
    if (!formData.salesRepId) {
      newError.salesRepId = "Sales rep is required";
    }
    if (!formData.leadSource) {
      newError.leadSource = "Lead source is required";
    }
    if (formData.tradeType.length === 0) {
      newError.tradeType = "Trade type is required";
    }
    if (!formData.jobDescription.trim()) {
      newError.jobDescription = "Job description is required";
    }

    if (formData.numberOfSquares && !squaresValuePattern.test(formData.numberOfSquares)) {
      newError.numberOfSquares = "# of Squares must be a number or fraction";
    }
    if (formData.pitch && !pitchValuePattern.test(formData.pitch)) {
      newError.pitch = "Pitch must be a number, 1/3, or 7-12";
    }

    if (formData.insuranceEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.insuranceEmail)) {
      newError.insuranceEmail = "Email is invalid";
    }
    if (formData.claimNumber && !/^\d+$/.test(formData.claimNumber)) {
      newError.claimNumber = "Claim number must be numeric";
    }
    if (formData.insurancePhoneNumber && !/^\d+$/.test(formData.insurancePhoneNumber)) {
      newError.insurancePhoneNumber = "Phone number must be numeric";
    }

    setErrors(newError);
    return Object.values(newError).every((v) => v === "");
  }, [formData]);

  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setErrors(initialErrors);
  }, []);

  return {
    formData,
    errors,
    updateField,
    updateError,
    validateJobStep,
    validateDetailsStep,
    isDetailsStepValid,
    resetForm,
  };
};
